import React, { useMemo, useEffect, useRef } from "react";
import "./styles.css";
import FloatingActions, { FloatingAction } from "../FloatingActions";
import { AgGridReact } from "ag-grid-react";
import { ColDef, IDatasource, IGetRowsParams, RowSelectionOptions, SelectionChangedEvent, themeQuartz } from "ag-grid-community";
import { AG_GRID_LOCALE_CZ } from '@ag-grid-community/locale';
import useAppData from "@/store/useAppData";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { RouterStoreItemKey } from "@/store/useRouter/types";
import { faArrowLeft, faWarehouse, faTrash } from "@fortawesome/free-solid-svg-icons";
import useRouter from "@/store/useRouter";
import { useShallow } from "zustand/shallow";
import { FilterKind, Item, ListScope, Stock, UiColumn } from "../ItemList/types";
import axios from "axios";
import { DeicingItem } from "./types";
import Card from "../Card";
import useDeviceType from "@/Utils/useDeviceType";
import DialogWindow from "../DialogWindow";

// const myTheme = themeQuartz.withParams({
//     fontSize: 17,
//     headerFontSize: 17,
//     rowHeight: 55,
//     headerHeight: 48,
// });

const desktopTheme = themeQuartz.withParams({
    fontSize: 17,
    headerFontSize: 17,
    rowHeight: 55,
    headerHeight: 48,
});

const tabletTheme = themeQuartz.withParams({
    fontSize: 15,
    headerFontSize: 15,
    rowHeight: 70,
    headerHeight: 46,
});

const mobileTheme = themeQuartz.withParams({
    fontSize: 14,
    headerFontSize: 14,
    rowHeight: 80,
    headerHeight: 48,
});

function mapFilter(filter?: FilterKind): ColDef["filter"] {
    if (filter === "text") return "agTextColumnFilter";
    if (filter === "number") return "agNumberColumnFilter";
    if (filter === "set") return "agSetColumnFilter";
    return false;
}


export interface DeicingListProps {
    onSettingsClick?: () => void;
    onProfileClick?: () => void;
    onChangedScope: (scope: ListScope) => void;
    scope: string;
}

const ItemListDeicing: React.FC<DeicingListProps> = ({ onSettingsClick, onProfileClick, onChangedScope, scope }: DeicingListProps) => {

    const loggedUser = useAppData(state => state.user);
    const setSelectedDeicing = useAppData(state => state.setSelectedDeicing);
    const selectedDeicing = useAppData(state => state.selectedDeicing);
    const selectedStock = useAppData(state => state.selectedStock);
    const setSelectedStock = useAppData(state => state.setSelectedStock);
    const selectedModule = useAppData(state => state.selectedModule);
    const deviceType = useDeviceType();
    const gridRef = useRef<AgGridReact<DeicingItem>>(null);
    const [stocksState, setStocksState] = React.useState<Stock[]>([]);
    const [deleteTarget, setDeleteTarget] = React.useState<string | null>(null);
    const [deleting, setDeleting] = React.useState(false);
    const [deleteError, setDeleteError] = React.useState<string | null>(null);
    const [selectingRepeat, setSelectingRepeat] = React.useState(false);
    const [repeatSource, setRepeatSource] = React.useState<string | null>(null);

    const router = useRouter(
        useShallow(state => ({
            go: state.go,
            history: state.history
        }))
    );

    const rowHeight = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return 80;

            case "tablet":
                return 70;

            default:
                return 55;
        }
    }, [deviceType]);

    const dateFormat = (date: string, dateOnly?: boolean) => {
        const d = new Date(date);

        const pad = (n: number) => String(n).padStart(2, "0");

        const formattedDate =
            pad(d.getUTCDate()) + "." +
            pad(d.getUTCMonth() + 1) + "." +
            d.getUTCFullYear();

        if (dateOnly) {
            return formattedDate;
        }

        return (
            formattedDate + " " +
            pad(d.getUTCHours()) + ":" +
            pad(d.getUTCMinutes()) + ":" +
            pad(d.getUTCSeconds())
        );
    };

    // const dateFormat = (date: string, dateOnly?: boolean) => {
    //     const d = new Date(date);

    //     const pad = (n: number) => String(n).padStart(2, "0");

    //     if (dateOnly) {
    //         return (
    //             pad(d.getDate()) + "." +
    //             pad(d.getMonth() + 1) + "." +
    //             d.getFullYear()
    //         );
    //     } else {
    //         return (
    //             pad(d.getDate()) + "." +
    //             pad(d.getMonth() + 1) + "." +
    //             d.getFullYear() + " " +
    //             pad(d.getHours()) + ":" +
    //             pad(d.getMinutes()) + ":" +
    //             pad(d.getSeconds())
    //         );
    //     }
    // };

    // useEffect(() => {
    //     gridRef.current?.api.resetRowHeights();
    // }, [deviceType]);

    useEffect(() => {
        loadStocks();
    }, [selectedModule?.id]);

    const loadStocks = async () => {

        try {
            const res = await (
                axios({
                    method: "post",
                    url: "api/stock/getAll",
                    data: {
                        userId: loggedUser?.id,
                        module: selectedModule?.id
                    }
                })
            );

            if (!res?.data?.stocks?.length || res?.data?.stocks?.length === 0) {
                // setSelectedStockState(undefined);
                setSelectedStock(null);

                setStocksState([]);

                return;
            }

            setStocksState(res.data.stocks);

            if (selectedStock && res.data.stocks.find(stock => stock.id === selectedStock.id)) {
                // setSelectedStockState(selectedStock?.id);
            } else {
                // setSelectedStockState(String(res.data.stocks[0].id))
                setSelectedStock(res.data.stocks[0]);
            }

        } catch (err) {
            throw err;
        }
    }

    const gridTheme = useMemo(() => {
        switch (deviceType) {
            case "mobile":
                return mobileTheme;

            case "tablet":
                return tabletTheme;

            default:
                return desktopTheme;
        }
    }, [deviceType]);

    const columns = useMemo<UiColumn<DeicingItem>[]>(() => [
        {
            field: "date",
            headerName: "Datum",
            flex: 0.8,
            minWidth: 80,
            filter: "text",
            floatingFilter: true,
            mobile: true,
            tablet: false,
            desktop: false,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value, true);
            }
        },
        {
            field: "type1Start",
            headerName: "Začátek I",
            flex: 1,
            minWidth: 115,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        {
            field: "type1End",
            headerName: "Konec I",
            flex: 1,
            minWidth: 115,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        // {
        //     field: "type1FluidConsumption",
        //     headerName: "Kapalina I",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "fluidRatio",
        //     headerName: "Poměr I",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        {
            field: "type2Start",
            headerName: "Začátek II",
            flex: 1,
            minWidth: 120,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        {
            field: "type2End",
            headerName: "Konec II",
            flex: 1,
            minWidth: 120,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        },
        // {
        //     field: "type2FluidConsumption",
        //     headerName: "Kapalina II",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        // {
        //     field: "waterConsumption",
        //     headerName: "Voda",
        //     flex: 0.8,
        //     minWidth: 80,
        //     filter: "number",
        //     floatingFilter: true,
        //     desktop: true
        // },
        {
            field: "aircraftRegistration",
            headerName: "Imatrikulace",
            flex: 1,
            minWidth: 80,
            filter: "text",
            floatingFilter: true,
            mobile: true,
            tablet: true,
            desktop: true
        },
        {
            field: "checked",
            headerName: "Kontrola",
            flex: 0.7,
            minWidth: 70,
            filter: "set",
            floatingFilter: true,
            mobile: true,
            tablet: true,
            desktop: true,
            valueFormatter: params => params.value ? "Ano" : "Ne"
        },
        {
            field: "modifiedByName",
            headerName: "Upravil",
            flex: 1,
            minWidth: 120,
            filter: "text",
            floatingFilter: true,
            tablet: true,
            desktop: true
        },
        {
            field: "lastModified",
            headerName: "Upraveno",
            flex: 1,
            minWidth: 120,
            tablet: true,
            desktop: true,
            valueFormatter: params => {
                if (!params.value) return "";

                return dateFormat(params.value);
            }
        }
    ], []);

    // const columns: UiColumn<DeicingItem>[] = [
    //     // { field: "id", headerName: "Id", flex: 1, filter: "number", floatingFilter: true },
    //     { field: "type1Start", headerName: "Začátek odmrazování I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type1End", headerName: "Konec odmrazování I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type1FluidConsumption", headerName: "Spotřeba kapaliny I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "fluidRatio", headerName: "Poměr kapaliny I", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2Start", headerName: "Začátek odmrazování II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2End", headerName: "Konec odmrazování II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "type2FluidConsumption", headerName: "Spotřeba kapaliny II", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "waterConsumption", headerName: "Spotřeba vody", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "stand", headerName: "Stojánka", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "company", headerName: "Společnost", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "aircraftType", headerName: "Typ letadla", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "aircraftRegistration", headerName: "Imatrikulace letadla", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "flightNumber", headerName: "Číslo letu", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "oat", headerName: "OAT", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "wingTemperature", headerName: "Teplota křídel", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "weatherCode", headerName: "Počasí", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "supervisor", headerName: "Sluchátka", flex: 3, filter: "text", floatingFilter: true },
    //     { field: "checked", headerName: "Zkontrolováno", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "checkedBy", headerName: "Zkontroloval", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "lastModified", headerName: "Upraveno", flex: 3, filter: "text", floatingFilter: true },
    //     // { field: "modifiedBy", headerName: "Upravil", flex: 3, filter: "text", floatingFilter: true },
    // ];

    const deleteDeicing = async () => {
        if (!deleteTarget || deleting || loggedUser?.userrole !== "admin") return;
        setDeleting(true);
        setDeleteError(null);
        try {
            await axios.post("api/deicing/delete", { id: deleteTarget });
            gridRef.current?.api.deselectAll();
            setSelectedDeicing(null);
            gridRef.current?.api.purgeInfiniteCache();
        } catch (error) {
            setDeleteError(axios.isAxiosError(error)
                ? error.response?.data?.message ?? "Odmraz se nepodařilo smazat. Zkuste to prosím znovu."
                : "Odmraz se nepodařilo smazat. Zkuste to prosím znovu.");
        } finally {
            setDeleting(false);
        }
    };

    const actions: FloatingAction[] = selectingRepeat ? [
        ...(repeatSource ? [{
            key: "confirmRepeat",
            label: "Vybrat",
            onClick: () => {
                setSelectedDeicing(repeatSource);
                router.go({ key: RouterStoreItemKey.DEICINGCARD, data: { code: "repeatDeicing" } });
            }
        }] : []),
        {
            key: "cancelRepeat",
            label: "Zpět",
            onClick: () => {
                setRepeatSource(null);
                setSelectingRepeat(false);
            }
        }
    ] : [
        ...(loggedUser?.userrole === "admin" && selectedDeicing && !deleting ? [{
            key: "delete",
            label: "Smazat",
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: () => {
                const row = gridRef.current?.api.getSelectedRows()[0];
                if (!row) return;
                setDeleteError(null);
                setDeleteTarget(String(row.id));
            },
        }] : []),
        ...(loggedUser?.userrole != "viewer" ? [{
            key: "new",
            label: "Nový",
            //icon: <FontAwesomeIcon icon={faWarehouse} />,
            onClick: () => {
                setSelectedDeicing(null);
                router.go({ key: RouterStoreItemKey.DEICINGCARD });
            }
        }] : []),
        ...(loggedUser?.userrole != "viewer" ? [{
            key: "repeat",
            label: "Opakovaný",
            //icon: <FontAwesomeIcon icon={faWarehouse} />,
            onClick: () => {
                setSelectedDeicing(null);
                setRepeatSource(null);
                setSelectingRepeat(true);
            }
        }] : []),
        ...(loggedUser?.userrole != "viewer" && selectedDeicing ? [{
            key: "edit",
            label: "Upravit",
            //icon: <FontAwesomeIcon icon={faWarehouse} />,
            onClick: () => {
                router.go({ key: RouterStoreItemKey.DEICINGCARD });
            }
        }] : []),
        // {
        //     key: "2",
        //     label: "Zpět",
        //     icon: <FontAwesomeIcon icon={faArrowLeft} />,
        //     onClick: () => { router.go({ key: RouterStoreItemKey.LISTITEMS }); }
        // }
    ];

    const datasource = useMemo<IDatasource>(() => ({
        getRows: async (params: IGetRowsParams) => {
            try {

                const result = await getDeicings(
                    params.startRow,
                    params.endRow,
                    { ...params }
                );

                params.successCallback(result.rows, result.totalCount);
            } catch {
                params.failCallback();
            }
        },
    }), [selectedStock?.id, selectingRepeat]);

    const getDeicings = async (startRow, endRow, agParams) => {
        try {
            console.log(agParams);
            const res = await (
                axios({
                    method: "post",
                    url: "api/deicing/getAll",
                    data: {
                        start: startRow,
                        end: endRow,
                        filterModel: agParams.filterModel,
                        sortModel: selectingRepeat
                            ? [...(agParams.sortModel?.length ? agParams.sortModel : [{ colId: "date", sort: "desc" }]),
                                { colId: "id", sort: "desc" }]
                            : agParams.sortModel,
                        stockId: selectingRepeat ? undefined : selectedStock?.id,
                        repeatCandidates: selectingRepeat
                    }
                })
            );

            return {
                rows: res.data.deicingItems,
                totalCount: res.data.totalCount
            };
        } catch (err) {
            console.log(err);
            throw err;
        }
    }

    // const handleSelectionChanged = (e) => {
    //     if (e.api.getSelectedRows()?.length > 0) {
    //         const a = e.api.getSelectedRows();
    //         setSelectedDeicing(e.api.getSelectedRows()[0]?.id);
    //     }
    // }

    const handleSelectionChanged = (
        event: SelectionChangedEvent<DeicingItem>
    ) => {
        const [selectedRow] = event.api.getSelectedRows();

        if (selectingRepeat) {
            setRepeatSource(selectedRow?.id ?? null);
        } else {
            setSelectedDeicing(selectedRow?.id ?? null);
        }
    };

    // const columnDefs = useMemo<ColDef<Item>[]>(() => {

    //     return columns.map((c) => ({
    //         field: c.field as any,
    //         headerName: c.headerName,
    //         flex: c.flex,
    //         filter: mapFilter(c.filter),
    //         floatingFilter: c.floatingFilter,
    //         valueFormatter: c?.valueFormatter,
    //         checkboxSelection: c?.checkboxSelection,
    //         headerCheckboxSelection: c?.headerCheckboxSelection,
    //         maxWidth: c?.maxWidth,
    //         editable: c?.editable
    //     }));
    // }, [columns]);

    const columnDefs = useMemo<ColDef<DeicingItem>[]>(() => {
        if (selectingRepeat) {
            const repeatColumns: ColDef<DeicingItem>[] = [
                { field: "date", headerName: "Datum", minWidth: 125, flex: 1, sort: "desc",
                    valueFormatter: params => params.value ? dateFormat(params.value, true) : "" },
                { field: "aircraftRegistration", headerName: "Imatrikulace", minWidth: 140, flex: 1 },
                { field: "flightNumber", headerName: "Číslo letu", minWidth: 120, flex: 1 },
                { field: "stockName", headerName: "Vozidlo", minWidth: 120, flex: 1 }
            ];
            return repeatColumns.map(column => ({
                ...column,
                sortable: true,
                filter: "agTextColumnFilter",
                floatingFilter: deviceType !== "mobile",
                resizable: deviceType !== "mobile"
            }));
        }
        return columns
            .filter(column => column[deviceType] === true)
            .map(column => ({
                field: column.field as keyof DeicingItem,
                headerName: column.headerName,
                flex: /*deviceType === "mobile" ? undefined :*/ column.flex,
                //width: deviceType === "mobile" ? column.minWidth : undefined,
                minWidth: column.minWidth,
                filter: mapFilter(column.filter),

                // Floating filters take too much vertical space on mobile.
                floatingFilter:
                    deviceType !== "mobile" &&
                    Boolean(column.floatingFilter),

                //wrapText: deviceType === "mobile",
                //autoHeight: true, //deviceType === "mobile",

                valueFormatter: column.valueFormatter,
                checkboxSelection: column.checkboxSelection,
                headerCheckboxSelection: column.headerCheckboxSelection,
                maxWidth: column.maxWidth,
                editable: column.editable,

                sortable: true,
                resizable: deviceType !== "mobile",

                // Shows complete column name on narrow displays.
                headerTooltip: column.headerName
            }));
    }, [columns, deviceType, selectingRepeat]);

    // const rowSelection: RowSelectionOptions<Item> = {
    //     mode: "singleRow",
    //     checkboxes: false,
    //     enableClickSelection: true,
    // };

    const rowSelection = useMemo<RowSelectionOptions<DeicingItem>>(() => ({
        mode: "singleRow",
        checkboxes: false,
        enableClickSelection: true
    }), []);

    const defaultColDef = useMemo<ColDef<DeicingItem>>(() => ({
        sortable: true,
        filter: true,
        suppressHeaderMenuButton: deviceType === "mobile"
    }), [deviceType]);

    const onChangeSelectedStock = (e) => {
        const stock = stocksState.find(
            stock => String(stock.id) === e.target.value
        );

        setSelectedStock(stock!);
        gridRef.current?.api.deselectAll();
        setSelectedDeicing(null);
        setDeleteError(null);
        // setSelectedStock(stocksState.filter(stock => stock.id == e.target.value)[0]);
        // setSelectedStockState(e.target.value);

        // gridApiRef?.current?.deselectAll();
        // gridApiRef?.current?.refreshInfiniteCache();
    }

    return <div className="deicing deicing-list"><Card
        title={selectingRepeat ? "Opakovaný odmraz – výběr odmrazu" : "Odmrazy"}
        displaySelectedStock={false}
        displayLogo={false}
        //onSettingsClick={onSettingsClick}
        displayProfile={true}
    >
        <div className="item-list-card">
            {deleteTarget && <DialogWindow
                displayDialog={true}
                setDisplayDialog={display => { if (!display) setDeleteTarget(null); }}
                title={`Smazat odmraz č. ${deleteTarget} včetně všech jeho položek? Tuto akci nelze vrátit.`}
                confirmText="Smazat"
                cancelText="Zrušit"
                onConfirm={deleteDeicing}
            />}
            {deleteError && <div className="form-error" role="alert">{deleteError}</div>}

            {/* <div className="item-list-toolbar">
                <div className="item-list-toolbar-right">
                    <select
                        value={selectedStock?.id}
                        onChange={onChangeSelectedStock}
                    >
                        {stocksState.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div> */}

            {!selectingRepeat && <div className="item-list-toolbar">

                {loggedUser?.userrole === "admin" && <button
                    type="button"
                    className={`item-list-tab ${scope === "deicingOperator" ? "is-active" : ""}`}
                    onClick={() => { onChangedScope("deicingOperator"); }}
                >
                    Operátor
                </button>}
                {loggedUser?.userrole === "admin" && <button
                    type="button"
                    className={`item-list-tab ${scope === "deicingCheck" ? "is-active" : ""}`}
                    onClick={() => { onChangedScope("deicingCheck"); }}
                >
                    Kontrola
                </button>}

                <div className="item-list-toolbar-right">
                    <select
                        value={selectedStock?.id}
                        onChange={onChangeSelectedStock}
                    >
                        {stocksState.map((o) => (
                            <option key={o.id} value={o.id}>
                                {o.name}
                            </option>
                        ))}
                    </select>
                </div>
            </div>}

            <div className="item-list-grid-wrapper">
                {/* <AgGridReact
                    theme={myTheme}
                    columnDefs={columnDefs}
                    rowModelType="infinite"
                    cacheBlockSize={100}
                    datasource={datasource}
                    localeText={AG_GRID_LOCALE_CZ}
                    // suppressRowClickSelection={true}
                    getRowId={(params) => params.data.id}
                    onSelectionChanged={handleSelectionChanged}
                    rowSelection={rowSelection}
                /> */}
                <AgGridReact<DeicingItem>
                    key={selectingRepeat ? "repeat" : "list"}
                    ref={gridRef}
                    theme={gridTheme}
                    columnDefs={columnDefs}
                    defaultColDef={defaultColDef}

                    rowModelType="infinite"
                    cacheBlockSize={/*deviceType === "mobile" ? 50 : 100*/ 100}
                    datasource={datasource}
                    rowHeight={rowHeight}

                    localeText={AG_GRID_LOCALE_CZ}
                    getRowId={params => String(params.data.id)}

                    rowSelection={rowSelection}
                    onSelectionChanged={handleSelectionChanged}

                    animateRows={/*deviceType !== "mobile"*/ true}
                    suppressCellFocus={/*deviceType === "mobile"*/ true}
                    suppressMovableColumns={/*deviceType !== "desktop"*/ true}

                    tooltipShowDelay={300}
                    tooltipHideDelay={3000}
                />
            </div>

            {
                actions?.length ? (
                    <div className="item-list-actions">
                        <FloatingActions actions={actions} />
                    </div>
                ) : null
            }
        </div >
    </Card></div>;
};

export default ItemListDeicing;
